import type { EventHeader, InplayStatus, LeagueId, LivePauseKind, LiveSide, MarketSpec, StakeKind } from "@/lib/odds-screen/live/types";

export function trimLine(value: number): string {
  return String(Number(value.toFixed(2)));
}

export function formatUsd(value: number): string {
  if (Math.round(value) < 1000) return `$${Math.round(value)}`;
  if (value < 9950) return `$${(value / 1000).toFixed(1)}k`;
  if (value < 999_500) return `$${Math.round(value / 1000)}k`;
  return `$${(value / 1_000_000).toFixed(1)}M`;
}

export type StakeDisplay = { readonly text: string; readonly spoken: string; readonly tone: "depth" | "limit" };

export function formatStake(stake: number, kind: StakeKind): StakeDisplay;
export function formatStake(stake: number | null | undefined, kind: StakeKind | null): StakeDisplay | null;
export function formatStake(stake: number | null | undefined, kind: StakeKind | null): StakeDisplay | null {
  if (stake === null || stake === undefined) return null;
  const usd = formatUsd(stake);
  return kind === "limit"
    ? { text: `${usd} max`, spoken: `${usd} max`, tone: "limit" }
    : { text: `${usd} liq`, spoken: `${usd} liquidity`, tone: "depth" };
}

export function signedLine(value: number): string {
  if (value === 0) return "PK";
  return `${value > 0 ? "+" : "-"}${trimLine(Math.abs(value))}`;
}

export function shortTeam(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] || name;
}

export type RowLine = "away" | "draw" | "home";

const TWO_WAY_LINES: readonly RowLine[] = ["away", "home"];
const THREE_WAY_LINES: readonly RowLine[] = ["away", "draw", "home"];

export function rowLines(specs: readonly MarketSpec[], hasEntity: boolean): readonly RowLine[] {
  return !hasEntity && specs.some((spec) => spec.outcomes.includes("draw")) ? THREE_WAY_LINES : TWO_WAY_LINES;
}

export type SetCell = { readonly games: number; readonly state: "won" | "lost" | "current" };
export type PeriodCell = { readonly points: number; readonly current: boolean };
export type SideLive = { readonly score: string | null; readonly sets: readonly SetCell[] | null; readonly periods: readonly PeriodCell[] | null; readonly serving: boolean | null; readonly trailing: boolean };
export type MatchupSide = { readonly line: RowLine; readonly name: string; readonly logo: string | null; readonly live: SideLive | null };

function betweenSets({ period, sets }: InplayStatus): boolean {
  return period !== null && (period.break || (period.unit === "set" && sets !== null && sets.games.length < period.number));
}

function homeLead(live: InplayStatus): number {
  if (live.sets) {
    const between = betweenSets(live);
    const games = live.sets.games;
    const current = games[games.length - 1];
    return (between ? games : games.slice(0, -1)).reduce((sets, set) => sets + Math.sign(set.home - set.away), 0) || (between ? 0 : current.home - current.away);
  }
  if (live.score === null || live.period?.unit === "set") return 0;
  return Number(live.score.home) - Number(live.score.away) || 0;
}

function sideLive(live: InplayStatus, line: "away" | "home"): SideLive {
  const lead = homeLead(live);
  const between = betweenSets(live);
  const sets = live.sets?.games.map((set, i, games): SetCell => {
    const own = set[line];
    const other = line === "home" ? set.away : set.home;
    return { games: own, state: i === games.length - 1 && !between ? "current" : own > other ? "won" : "lost" };
  }) ?? null;
  const boxed = live.period !== null && !live.period.break && !["overtime", "extra_time", "shootout"].includes(live.period.unit) && live.periods?.length === live.period.number;
  const periods = sets === null ? live.periods?.map((pair, i, all): PeriodCell => ({ points: pair[line], current: boxed && i === all.length - 1 })) ?? null : null;
  const serving = live.sets?.serving ?? null;
  return { score: live.score?.[line] ?? null, sets, periods, serving: serving === null ? null : serving === line, trailing: line === "home" ? lead < 0 : lead > 0 };
}

export function matchupSides(event: EventHeader | undefined, lines: readonly RowLine[], draw = "Draw"): readonly MatchupSide[] {
  return lines.map((line): MatchupSide => {
    if (line === "draw") return { line, name: draw, logo: null, live: null };
    const live = event?.live ? sideLive(event.live, line) : null;
    return line === "away"
      ? { line, name: event?.away ?? "", logo: event?.awayLogoUrl ?? null, live }
      : { line, name: event?.home ?? "", logo: event?.homeLogoUrl ?? null, live };
  });
}

const INDIVIDUAL_LEAGUES: ReadonlySet<LeagueId> = new Set(["golf", "tennis", "mma", "darts"]);
const NICKNAME_LEAGUES: ReadonlySet<LeagueId> = new Set(["nfl", "nba", "wnba", "nhl", "mlb"]);

function shortSide(league: LeagueId, name: string, abbreviation: string | null): string {
  if (INDIVIDUAL_LEAGUES.has(league) || NICKNAME_LEAGUES.has(league)) return shortTeam(name);
  return abbreviation ?? name;
}

export type MatchupStyle = "short" | "full" | "spoken";

export function isTournament(event: Pick<EventHeader, "home" | "away">): boolean {
  return event.home === "" && event.away === "";
}

function versus(league: LeagueId, form: MatchupStyle | "hosting"): string {
  if (INDIVIDUAL_LEAGUES.has(league) || form === "hosting") return "vs";
  return form === "spoken" ? "at" : "@";
}

type SideNames = Readonly<Record<LiveSide, string>>;

function sideNames(event: EventHeader): SideNames {
  return event.homeShort !== null && event.awayShort !== null ? { home: event.homeShort, away: event.awayShort } : { home: event.home, away: event.away };
}

export const PAUSE_LABELS: Readonly<Record<LivePauseKind, string>> = {
  period_break: "Break", timeout: "Timeout", tv_timeout: "TV timeout", medical_timeout: "Medical timeout", review: "Review", delay: "Delay", game_break: "Between games", other: "Stopped",
};

export function matchupLabel(event: EventHeader | undefined, league: LeagueId, style: MatchupStyle = "short"): string {
  if (event === undefined) return "";
  if (isTournament(event)) return event.name;
  const joiner = versus(league, style);
  const away = style === "short" ? shortSide(league, event.away, event.awayShort) : event.away;
  const home = style === "short" ? shortSide(league, event.home, event.homeShort) : event.home;
  return away === home ? `${event.away} ${joiner} ${event.home}` : `${away} ${joiner} ${home}`;
}

export function scoreLine(event: EventHeader): string | null {
  const score = event.live?.score;
  if (!score || isTournament(event)) return null;
  const names = sideNames(event);
  return `${names.away} ${score.away}-${score.home} ${names.home}`;
}

export function scoredMatchupLabel(event: EventHeader | undefined, league: LeagueId): string {
  const score = event?.live?.score;
  if (event === undefined || !score || isTournament(event)) return matchupLabel(event, league, "spoken");
  return `${event.away} ${score.away} ${versus(league, "spoken")} ${event.home} ${score.home}`;
}

export function opponentLine(event: EventHeader | undefined, team: string, league: LeagueId): string {
  if (event === undefined) return "";
  if (team === event.away) return `${versus(league, "short")} ${event.home}`;
  if (team === event.home) return `${versus(league, "hosting")} ${event.away}`;
  return matchupLabel(event, league, "full");
}

export function teamLogoUrl(event: EventHeader | undefined, name: string): string | null {
  if (!event) return null;
  return name === event.home ? event.homeLogoUrl : name === event.away ? event.awayLogoUrl : null;
}

export function outcomeDisplayOrder(spec: MarketSpec): readonly number[] {
  const { outcomes } = spec;
  const index = (name: string) => outcomes.indexOf(name);
  const over = index("over");
  const under = index("under");
  if (outcomes.length === 2 && over !== -1 && under !== -1) return [over, under];
  const home = index("home");
  const away = index("away");
  const draw = index("draw");
  if (home !== -1 && away !== -1 && outcomes.length === (draw === -1 ? 2 : 3)) return draw === -1 ? [away, home] : [away, draw, home];
  return outcomes.map((_, i) => i);
}

const OUTCOME_LINES: ReadonlyMap<string, RowLine> = new Map<string, RowLine>([
  ["away", "away"],
  ["over", "away"],
  ["draw", "draw"],
  ["home", "home"],
  ["under", "home"],
]);

export function outcomeLine(spec: MarketSpec, outcome: number): RowLine | undefined {
  return OUTCOME_LINES.get(spec.outcomes[outcome]);
}

export function cellLineLabel(spec: MarketSpec, main: number | null, outcome: number): string {
  if (spec.line === "none" || main === null) return "";
  const name = spec.outcomes[outcome];
  if (spec.line === "spread") {
    const value = name === "away" ? -main : main;
    return signedLine(value);
  }
  return `${name === "under" ? "u" : "o"}${trimLine(main)}`;
}

export function titleCaseKey(name: string): string {
  return name.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

function fillPlaceholders(label: string, event: EventHeader | undefined): string {
  return label.replace(/\{(home|away)\}/g, (_, side: "home" | "away") => {
    if (event === undefined) return side === "home" ? "Home" : "Away";
    return side === "home" ? event.homeShort || shortTeam(event.home) : event.awayShort || shortTeam(event.away);
  });
}

export function outcomeName(spec: MarketSpec, event: EventHeader | undefined, outcome: number): string {
  const label = spec.labels?.[outcome];
  if (label) return fillPlaceholders(label, event);
  const name = spec.outcomes[outcome];
  if (name === "home") return event ? event.home : "Home";
  if (name === "away") return event ? event.away : "Away";
  return titleCaseKey(name);
}

export function drawName(specs: readonly MarketSpec[]): string {
  for (const spec of specs) {
    const index = spec.outcomes.indexOf("draw");
    if (index !== -1) return outcomeName(spec, undefined, index);
  }
  return "Draw";
}

export function outcomeSideLabel(spec: MarketSpec, main: number | null, event: EventHeader | undefined, outcome: number): string {
  const name = spec.outcomes[outcome];
  if (spec.line === "total") {
    const side = name === "under" ? "Under" : "Over";
    return main === null ? side : `${side} ${trimLine(main)}`;
  }
  if (spec.line === "spread" && main !== null) {
    const value = name === "away" ? -main : main;
    return `${shortTeam(outcomeName(spec, event, outcome))} ${signedLine(value)}`;
  }
  return outcomeName(spec, event, outcome);
}
